import type { FeedIssue } from '@Store/api/product'

const FALLBACK_MESSAGE =
  'Check the URL opens in your browser, and add any credentials it needs.'

/**
 * What to tell the retailer when the request never produced our own answer.
 * A 504 is the gateway giving up on a slow read, not a verdict on the feed,
 * so it gets "try again" rather than "check your URL".
 */
function messageFor(error: any): string {
  const message = error?.data?.message
  if (typeof message === 'string' && message) return message

  if (error?.status === 504 || error?.status === 'TIMEOUT_ERROR') {
    return 'Your feed took too long to respond. Check it opens in your browser, then try again in a few minutes.'
  }

  return FALLBACK_MESSAGE
}

/** The excerpt with the character at fault marked, so it can be found. */
function Excerpt({ text, position }: { text: string; position?: number }) {
  const hasMark =
    typeof position === 'number' && position >= 0 && position < text.length

  return (
    <pre className="mt-2 mb-0 whitespace-pre-wrap break-all rounded-md bg-gray-50 border border-solid border-gray-200 px-3 py-2 font-mono text-xs text-gray-700">
      {hasMark ? (
        <>
          {text.slice(0, position)}
          <mark className="rounded-sm bg-error-50 px-0.5 font-semibold text-error-700 outline outline-1 outline-error-300">
            {text[position]}
          </mark>
          {text.slice(position + 1)}
        </>
      ) : (
        text
      )}
    </pre>
  )
}

/**
 * The failed-read state of the mapping dialog.
 *
 * When the file itself is malformed the backend sends `issues`: the line, the
 * column and an excerpt, which is what lets the retailer find the problem in
 * their own export. Otherwise its message already says what to check (the
 * URL, sharing, credentials), and that is shown as is. This used to be a fixed
 * "Failed to visit feed target." whatever had gone wrong.
 */
function FeedReadError({ error }: { error: any }) {
  const issues: FeedIssue[] = Array.isArray(error?.data?.issues)
    ? error.data.issues
    : []

  return (
    <div className="border border-solid border-gray-200 rounded-lg p-4">
      <div className="text-error-700 font-medium">
        We couldn't read your feed
      </div>

      {issues.length === 0 ? (
        <div className="text-gray-600 text-sm mt-1">{messageFor(error)}</div>
      ) : (
        <>
          <div className="text-gray-600 text-sm mt-1">
            Your feed file has a formatting problem. Fix it in your export, then
            try again.
          </div>
          <ul className="mt-3 mb-0 flex list-none flex-col gap-3 p-0">
            {issues.map((issue, index) => (
              <li
                key={`${issue.line}-${index}`}
                className="rounded-lg border border-solid border-error-300 bg-error-25 p-3"
              >
                <div className="text-xs font-medium text-error-700">
                  Line {issue.line}
                  {issue.column ? ` · column “${issue.column}”` : ''}
                </div>
                <div className="mt-1 text-sm text-gray-900">
                  {issue.problem}
                </div>
                {issue.excerpt ? (
                  <Excerpt text={issue.excerpt} position={issue.position} />
                ) : null}
                <div className="mt-2 text-sm text-gray-600">{issue.fix}</div>
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  )
}

export default FeedReadError
